import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Home as HomeIcon, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const nav = useNavigate();
  const { setSession } = useAuth();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [name, setName] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      setErr("This invite link is missing a token.");
      return;
    }
    axios
      .get(`${API}/team/invite-info/${token}`)
      .then((r) => {
        setInfo(r.data);
        setName(r.data.name || "");
      })
      .catch((e) =>
        setErr(e?.response?.data?.detail || "Invite link is invalid or expired")
      );
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/team/accept-invite`, {
        token, password: pw, name,
      });
      localStorage.setItem("realtify_token", data.access_token);
      setSession?.(data);
      toast.success(`Welcome to the team, ${data.user.name || data.user.email}!`);
      nav("/dashboard");
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Could not accept invite");
    } finally {
      setBusy(false);
    }
  };

  if (err) {
    return (
      <div className="min-h-screen grid place-items-center p-6" data-testid="invite-error">
        <div className="card-flat p-8 max-w-md text-center">
          <span className="app-eyebrow">/ Invite invalid</span>
          <h1 className="font-display text-3xl mt-3">Link expired</h1>
          <p className="text-[var(--app-text-muted)] text-sm mt-2">{err}</p>
          <p className="text-xs text-[var(--app-text-muted)] mt-3">
            Ask your team admin to send you a fresh invite.
          </p>
        </div>
      </div>
    );
  }

  if (!info) {
    return (
      <div className="min-h-screen grid place-items-center">
        <Loader2 className="animate-spin" size={20} />
      </div>
    );
  }

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-white">
      <div className="hidden md:flex flex-col justify-between bg-[var(--app-black)] text-white p-10 relative grain">
        <div className="flex items-center gap-2.5">
          <span className="inline-grid place-items-center w-10 h-10 rounded-full bg-white text-[var(--app-black)]">
            <HomeIcon size={18} strokeWidth={2.4} />
          </span>
          <div className="font-display text-xl">FlipPilot</div>
        </div>
        <div>
          <span className="app-eyebrow" style={{ color: "var(--app-accent-2)" }}>
            / You&apos;re invited
          </span>
          <h1 className="font-display text-5xl mt-3">
            Join <span style={{ color: "var(--app-accent-2)" }}>{info.invited_by_name}</span>&apos;s team.
          </h1>
          <p className="mt-4 text-white/70 max-w-md">
            You&apos;ve been invited as <strong className="text-white">{info.role_label}</strong>.
            Set your password to activate your account.
          </p>
        </div>
        <div className="text-xs text-white/50">© 2026 FlipPilot</div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-10">
        <form
          onSubmit={submit}
          className="w-full max-w-sm space-y-5"
          data-testid="accept-invite-form"
        >
          <div>
            <span className="app-eyebrow">/ Set your password</span>
            <h2 className="font-display text-3xl mt-2">Welcome</h2>
            <p className="text-sm text-[var(--app-text-muted)] mt-2">
              {info.email} · {info.role_label}
            </p>
          </div>
          <div>
            <Label>Your name</Label>
            <Input
              data-testid="accept-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="field-sharp h-11 mt-1"
              placeholder="Jane Doe"
            />
          </div>
          <div>
            <Label>Password (min 6 chars)</Label>
            <Input
              type="password"
              data-testid="accept-password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              className="field-sharp h-11 mt-1"
              required
              minLength={6}
              placeholder="••••••••"
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            className="app-btn app-btn-primary w-full h-11"
            data-testid="accept-submit"
          >
            {busy ? "Activating…" : "Activate account"}
          </button>
        </form>
      </div>
    </div>
  );
}
